// ============================================================================
// Module: agentUsage
//
// The one place the dashboard reads AGGREGATE agent token usage and $ cost
// from (Step 6 of docs/TOKEN_COST_ACCURACY_PLAN.md). Every widget that shows
// token totals or a $ figure goes through fetchTeamAgentUsageDaily() and the
// small aggregation helpers below instead of re-writing the query itself.
//
// Source: the get_team_agent_usage_daily RPC (see
// supabase/migrations/20261002103709_agent_usage_events_and_model_pricing.sql).
// It is SECURITY DEFINER, callable by any member of the team, and returns
// agent_usage_events rolled up per (agent, team-local usage_date, model,
// git_branch), plus the agent's current project_id.
//
// VISIBILITY IS ENFORCED SERVER-SIDE: admins see every agent, managers see
// agents in their managed pods, and plain members see only their own agents.
// Members cannot reconstruct team-wide model/project usage by calling the RPC
// directly outside the dashboard UI.
// There is no client-side manager scoping any more. cost_usd is priced SERVER-SIDE when each event is ingested and
// frozen on the row -- the browser never applies a rate any more. Prices are
// list price ("List-price estimate"), not the team's actual bill.
//
// agent_checkin is still the source for per-check-in TASK text, files
// changed and check-in counts; only token totals and $ moved here.
// ============================================================================

import { supabase } from './supabase'

export interface AgentUsageDailyRow {
  /** Null only for rows that are not attributable to a visible agent. */
  agentId: string | null
  /** The agent's current agents.project_id; null = no project ("Unassigned"). */
  projectId: string | null
  /** Team-local date (YYYY-MM-DD). */
  usageDate: string
  model: string
  gitBranch: string | null
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  /** Frozen list-price cost. null when nothing in the group could be priced. */
  costUsd: number | null
  eventCount: number
  /** Events priced by a family fallback rate (not an exact model match). */
  estimatedEventCount: number
  /** Events with no rate at all; they contribute nothing to costUsd. */
  unpricedEventCount: number
}

export interface UsageTotals {
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  /** input + output + cache read + cache write. */
  totalTokens: number
  /** Sum of the priced part. null when no event in the group was priced. */
  costUsd: number | null
  eventCount: number
  estimatedEventCount: number
  unpricedEventCount: number
}

// PostgREST may serialize bigint / numeric as strings -- always coerce.
const toNum = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

const toNullableNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** Maps one raw RPC row (snake_case, possibly stringly-typed) to a typed row. */
export function normalizeUsageRow(raw: Record<string, unknown>): AgentUsageDailyRow {
  return {
    agentId: raw.agent_id ? String(raw.agent_id) : null,
    projectId: raw.project_id ? String(raw.project_id) : null,
    usageDate: String(raw.usage_date ?? '').slice(0, 10),
    model: raw.model ? String(raw.model) : 'unknown',
    gitBranch: raw.git_branch ? String(raw.git_branch) : null,
    inputTokens: toNum(raw.input_tokens),
    outputTokens: toNum(raw.output_tokens),
    cacheReadTokens: toNum(raw.cache_read_tokens),
    cacheWriteTokens: toNum(raw.cache_write_tokens),
    costUsd: toNullableNum(raw.cost_usd),
    eventCount: toNum(raw.event_count),
    estimatedEventCount: toNum(raw.estimated_event_count),
    unpricedEventCount: toNum(raw.unpriced_event_count),
  }
}

// PostgREST caps every response (db-max-rows, 1000 by default) and an RPC
// result is no exception, so page through it with a stable order rather than
// silently truncating a long range.
const PAGE_SIZE = 1000
const MAX_PAGES = 50

/**
 * Loads daily usage for one team between two team-local dates (inclusive).
 * Throws on error. `agentIds` narrows the result server-side (PostgREST
 * filter on the function's result set). Agents the caller can't see in
 * detail come back with agent_id NULL, so they never match an `agentIds`
 * filter -- asking for a forbidden agent just yields no rows.
 */
export async function fetchTeamAgentUsageDaily(
  teamId: string,
  from: string,
  to: string,
  opts: { agentIds?: string[] } = {}
): Promise<AgentUsageDailyRow[]> {
  if (opts.agentIds && opts.agentIds.length === 0) return []

  const rows: AgentUsageDailyRow[] = []
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // Cast: src/types/supabase.ts predates this RPC.
    let query = (supabase.rpc as any)('get_team_agent_usage_daily', {
      p_team_id: teamId,
      p_from: from,
      p_to: to,
    })
    if (opts.agentIds) query = query.in('agent_id', opts.agentIds)
    const { data, error } = await query
      .order('usage_date', { ascending: true })
      .order('agent_id', { ascending: true, nullsFirst: true })
      .order('project_id', { ascending: true, nullsFirst: true })
      .order('model', { ascending: true })
      .order('git_branch', { ascending: true, nullsFirst: true })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1)
    if (error) throw error
    const batch = ((data ?? []) as Record<string, unknown>[]).map(normalizeUsageRow)
    rows.push(...batch)
    if (batch.length < PAGE_SIZE) break
  }
  return rows
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

export function rowTotalTokens(
  row: Pick<AgentUsageDailyRow, 'inputTokens' | 'outputTokens' | 'cacheReadTokens' | 'cacheWriteTokens'>
): number {
  return row.inputTokens + row.outputTokens + row.cacheReadTokens + row.cacheWriteTokens
}

export function emptyTotals(): UsageTotals {
  return {
    inputTokens: 0,
    outputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
    totalTokens: 0,
    costUsd: null,
    eventCount: 0,
    estimatedEventCount: 0,
    unpricedEventCount: 0,
  }
}

/** Adds `row` (a daily row or another totals object) into `into`, in place. */
export function addToTotals(into: UsageTotals, row: AgentUsageDailyRow | UsageTotals): UsageTotals {
  into.inputTokens += row.inputTokens
  into.outputTokens += row.outputTokens
  into.cacheReadTokens += row.cacheReadTokens
  into.cacheWriteTokens += row.cacheWriteTokens
  into.totalTokens = rowTotalTokens(into)
  if (row.costUsd !== null) into.costUsd = (into.costUsd ?? 0) + row.costUsd
  into.eventCount += row.eventCount
  into.estimatedEventCount += row.estimatedEventCount
  into.unpricedEventCount += row.unpricedEventCount
  return into
}

export function sumUsage(rows: Iterable<AgentUsageDailyRow>): UsageTotals {
  const totals = emptyTotals()
  for (const row of rows) addToTotals(totals, row)
  return totals
}

/**
 * Groups rows by an arbitrary key. Returning null from `keyOf` skips the row.
 */
export function groupUsage(
  rows: Iterable<AgentUsageDailyRow>,
  keyOf: (row: AgentUsageDailyRow) => string | null
): Map<string, UsageTotals> {
  const out = new Map<string, UsageTotals>()
  for (const row of rows) {
    const key = keyOf(row)
    if (key === null) continue
    let totals = out.get(key)
    if (!totals) {
      totals = emptyTotals()
      out.set(key, totals)
    }
    addToTotals(totals, row)
  }
  return out
}

/** Rows with agent_id null (agents the caller can't see in detail) are skipped. */
export const sumByAgent = (rows: Iterable<AgentUsageDailyRow>) => groupUsage(rows, (r) => r.agentId)
/** Map key sumByProject uses for rows with no project_id. */
export const UNASSIGNED_PROJECT_KEY = '__unassigned__'
/** By the row's project_id, null-agent rows included; null project -> UNASSIGNED_PROJECT_KEY. */
export const sumByProject = (rows: Iterable<AgentUsageDailyRow>) =>
  groupUsage(rows, (r) => r.projectId ?? UNASSIGNED_PROJECT_KEY)
export const sumByModel = (rows: Iterable<AgentUsageDailyRow>) => groupUsage(rows, (r) => r.model || 'unknown')
export const sumByDate = (rows: Iterable<AgentUsageDailyRow>) => groupUsage(rows, (r) => r.usageDate)
/** Rows with no branch are skipped. */
export const sumByBranch = (rows: Iterable<AgentUsageDailyRow>) => groupUsage(rows, (r) => r.gitBranch)

/** Part of the figure was priced with a family fallback rate. */
export const hasEstimatedCost = (t: Pick<UsageTotals, 'estimatedEventCount'>) => t.estimatedEventCount > 0
/** Some events could not be priced, so costUsd (if any) is a floor. */
export const hasUnpricedUsage = (t: Pick<UsageTotals, 'unpricedEventCount'>) => t.unpricedEventCount > 0

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** Browser-local YYYY-MM-DD. */
export function toDateKey(d: Date): string {
  return (
    d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  )
}

/** YYYY-MM-DD for `days` days before today (browser-local). */
export function daysAgoKey(days: number, now: Date = new Date()): string {
  const d = new Date(now)
  d.setDate(d.getDate() - days)
  return toDateKey(d)
}

// ---------------------------------------------------------------------------
// Dead-hook warning
// ---------------------------------------------------------------------------

export const STALE_AFTER_MS = 24 * 60 * 60 * 1000

export interface AgentActivityStatus {
  /** ISO timestamp, or end of the last active day when only a date is known. */
  lastActivityAt: string | null
  /**
   * 'exact' when read from raw rows, 'day' when only the RPC's date is known,
   * 'none' when the agent provably never reported, 'unknown' when the caller
   * can't see this agent's activity at all (never stale).
   */
  precision: 'exact' | 'day' | 'none' | 'unknown'
  isStale: boolean
}

/**
 * True when the agent has been around for more than 24h and nothing (no
 * usage event, no check-in) has arrived in the last 24h. A freshly installed
 * agent isn't flagged until it has had 24h to report.
 */
export function isAgentStale(
  lastActivityAt: string | null,
  agentCreatedAt: string | null,
  now: number = Date.now()
): boolean {
  if (lastActivityAt) return now - new Date(lastActivityAt).getTime() > STALE_AFTER_MS
  if (!agentCreatedAt) return true
  return now - new Date(agentCreatedAt).getTime() > STALE_AFTER_MS
}

const latestIso = (a: string | null, b: string | null): string | null => {
  if (!a) return b
  if (!b) return a
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b
}

/**
 * Most recent activity per agent, for the "hook may not be running" badge.
 *
 * - The RPC over the last 3 days gives a per-agent "last active DATE" -- but
 *   only for agents the caller may see in detail (others come back with
 *   agent_id NULL and can't be attributed).
 * - Admins/managers (`canReadRaw`): additionally read the newest
 *   agent_usage_events.occurred_at and agent_checkin.created_at per agent
 *   for an exact timestamp. RLS hides rows a manager can't see.
 * - `canSeeAllAgents` (admins): "no data at all" really means the agent never
 *   reported. For anyone else it may just mean the server hid that agent, so
 *   the answer is precision 'unknown' and NOT stale -- never flag an agent
 *   we simply aren't allowed to see.
 *
 * Date-level answers treat a day as "active until 23:59:59 local", so they
 * only flag an agent once it has had no activity since before yesterday --
 * they can under-warn by up to a day, never over-warn.
 */
export async function fetchAgentActivityStatus(
  teamId: string,
  agents: { id: string; created_at: string | null }[],
  opts: { canReadRaw: boolean; canSeeAllAgents?: boolean; now?: number }
): Promise<Map<string, AgentActivityStatus>> {
  const now = opts.now ?? Date.now()
  const result = new Map<string, AgentActivityStatus>()
  if (agents.length === 0) return result
  const agentIds = agents.map((a) => a.id)

  const lastDayByAgent = new Map<string, string>()
  try {
    const rows = await fetchTeamAgentUsageDaily(teamId, daysAgoKey(2, new Date(now)), toDateKey(new Date(now)), {
      agentIds,
    })
    for (const r of rows) {
      if (!r.agentId) continue
      const prev = lastDayByAgent.get(r.agentId)
      if (!prev || r.usageDate > prev) lastDayByAgent.set(r.agentId, r.usageDate)
    }
  } catch (err) {
    console.error('Failed to load recent agent usage:', err)
  }

  const exactByAgent = new Map<string, string | null>()
  if (opts.canReadRaw) {
    await Promise.all(
      agentIds.map(async (id) => {
        const [eventRes, checkinRes] = await Promise.all([
          (supabase.from as any)('agent_usage_events')
            .select('occurred_at')
            .eq('agent_id', id)
            .order('occurred_at', { ascending: false })
            .limit(1),
          (supabase.from as any)('agent_checkin')
            .select('created_at')
            .eq('agent_id', id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1),
        ])
        const eventAt = (eventRes?.data?.[0]?.occurred_at as string | undefined) ?? null
        const checkinAt = (checkinRes?.data?.[0]?.created_at as string | undefined) ?? null
        exactByAgent.set(id, latestIso(eventAt, checkinAt))
      })
    )
  }

  for (const agent of agents) {
    const exact = exactByAgent.get(agent.id) ?? null
    const day = lastDayByAgent.get(agent.id) ?? null
    let lastActivityAt: string | null = null
    let precision: AgentActivityStatus['precision'] = 'none'
    if (exact) {
      lastActivityAt = exact
      precision = 'exact'
    } else if (day) {
      lastActivityAt = new Date(day + 'T23:59:59').toISOString()
      precision = 'day'
    } else if (!opts.canSeeAllAgents) {
      result.set(agent.id, { lastActivityAt: null, precision: 'unknown', isStale: false })
      continue
    }
    result.set(agent.id, {
      lastActivityAt,
      precision,
      isStale: isAgentStale(lastActivityAt, agent.created_at, now),
    })
  }
  return result
}
